import {
  HttpMethod,
  createCustomApiCallAction,
} from '@activepieces/pieces-common';
import {
  PieceAuth,
  Property,
  createPiece,
} from '@activepieces/pieces-framework';
import { PieceCategory } from '@activepieces/shared';
import { getBrandsAction } from './lib/actions/get-brands';
import { getBrandsByIdAction } from './lib/actions/get-brands-by-id';
import { getCategoriesAction } from './lib/actions/get-categories';
import { getCategoriesByIdAction } from './lib/actions/get-categories-by-id';
import { getCategoriesWithFieldsAction } from './lib/actions/get-categories-with-fields';
import { getInventoryAction } from './lib/actions/get-inventory';
import { getProductAction } from './lib/actions/get-product';
import { getProductsAction } from './lib/actions/get-products';
import { getSampleProductsAction } from './lib/actions/get-sample-products';
import { getProductsByStyleIdAction } from './lib/actions/get-products-by-style-id';
import { getProductsByPartNumberAction } from './lib/actions/get-products-by-part-number';
import { getProductsByWarehousesAction } from './lib/actions/get-products-by-warehouses';
import { getProductsWithFieldsAction } from './lib/actions/get-products-with-fields';
import { getStylesAction } from './lib/actions/get-styles';
import { getSampleStylesAction } from './lib/actions/get-sample-styles';
import { getStylesByIdAction } from './lib/actions/get-styles-by-id';
import { getStylesByStyleIdAction } from './lib/actions/get-styles-by-style-id';
import { getStylesByPartNumberAction } from './lib/actions/get-styles-by-part-number';
import { getStylesWithFieldsAction } from './lib/actions/get-styles-with-fields';
import { searchProductsAction } from './lib/actions/search-products';
import { searchStylesAction } from './lib/actions/search-styles';
import { SSActiveWearAuth } from './lib/common';

// Export custom SSActiveWear client for advanced usage
export { SSActiveWearClient } from './lib/common';
export * from './lib/common';

const markdown = `
**API Key**:

1. Login to your SSActiveWear account
2. Go to Account Settings -> API Access
3. Generate a new API key
4. Copy the API key

**Account Number**:

Your SSActiveWear account number (this is your username/account identifier).
`;

export const ssActiveWearAuth = PieceAuth.CustomAuth({
  description: markdown,
  required: true,
  props: {
    apiKey: PieceAuth.SecretText({
      displayName: 'API Key',
      required: true,
    }),
    accountNumber: Property.ShortText({
      displayName: 'Account Number',
      required: true,
    }),
  },
  validate: async ({ auth }) => {
    try {
      const { SSActiveWearClient } = await import('./lib/common');
      const client = new SSActiveWearClient(auth as any);
      const isConnected = await client.testConnection();
      if (isConnected) {
        return { valid: true };
      } else {
        return { valid: false, error: 'Invalid credentials or API key' };
      }
    } catch (e) {
      return { valid: false, error: 'Invalid credentials or API key' };
    }
  },
});

const ssActiveWear = createPiece({
  displayName: 'SSActiveWear',
  description: 'SSActiveWear supplier integration for product and inventory management',
  logoUrl: 'https://cdn.activepieces.com/pieces/ssactivewear.png',
  authors: ["custom"],
  categories: [PieceCategory.COMMERCE],
  minimumSupportedRelease: '0.30.0',
  auth: ssActiveWearAuth,
  actions: [
    // Products
    getSampleProductsAction,
    getProductAction,
    getProductsAction,
    searchProductsAction,
    getProductsByStyleIdAction,
    getProductsByPartNumberAction,
    getProductsByWarehousesAction,
    getProductsWithFieldsAction,
    // Styles
    getSampleStylesAction,
    getStylesAction,
    getStylesByIdAction,
    getStylesByStyleIdAction,
    getStylesByPartNumberAction,
    getStylesWithFieldsAction,
    searchStylesAction,
    // Categories
    getCategoriesAction,
    getCategoriesByIdAction,
    getCategoriesWithFieldsAction,
    // Brands
    getBrandsAction,
    getBrandsByIdAction,
    // Inventory
    getInventoryAction,
    createCustomApiCallAction({
      baseUrl: () => 'https://api.ssactivewear.com/V2',
      auth: ssActiveWearAuth,
      authMapping: async (auth) => {
        const typedAuth = auth as SSActiveWearAuth;
        return {
          'X-Username': typedAuth.accountNumber,
          'X-Password': typedAuth.apiKey,
        };
      },
    }),
  ],
  triggers: [],
});

export { ssActiveWear };
export default ssActiveWear;
